/*
 * Sticky add to cart bar (snippets/cart-bar.liquid).
 *
 * Hidden while the main add to cart button is in view; shown once that button scrolls away.
 * Mobile/tablet: fixed to the bottom (body gets matching bottom padding). Desktop: top (docked under
 * the header on load, pinned to the top once the header scrolls away) or bottom, per data-desktop-position.
 *
 * The bar has no form of its own; it mirrors and drives the main product form of the closest <product-info>:
 * - Pack size <select>: built from <bar-packs> (snippets/product-bar-packs.liquid). Picking a pack checks the matching
 *   card input in the main form, which switches the variant (variant mode) or sets the quantity (quantity mode).
 *   Products without bar packs: one <select> per option, clicking the matching main option value.
 * - Purchase type radios: <purchase-options>.setPurchaseType(); the purchase box owns the form's selling_plan input.
 * - Prices from the `purchase-options:change` / `bar-packs:change` states (fallback: variant price x quantity).
 * - The button submits the main form with its own submit button, so selling_plan + quantity are sent and the usual
 *   add to cart flow (Rebuy Smart Cart) runs. Its label, disabled and loading state mirror the main button.
 */
(function () {
  'use strict';

  if (customElements.get('cart-bar')) return;

  const DESKTOP_QUERY = '(min-width: 1024px)';

  const formatMoney = (cents) => {
    const theme = window.theme || {};
    if (!theme.formatMoney) return `$${(cents / 100).toFixed(2)}`;
    const format = theme.settings?.currency_code_enable ? theme.moneyWithCurrencyFormat : theme.moneyFormat;
    return theme.formatMoney(cents, format);
  };

  const fill = (template, values) => String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  class CartBar extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('product-info');
      this.form = this.section?.querySelector('form[data-product-form]');
      if (!this.section || !this.form) return;

      this.inner = this.querySelector('[data-cart-bar-inner]');
      this.packWrapper = this.querySelector('[data-cart-bar-pack-wrapper]');
      this.packSelect = this.querySelector('[data-cart-bar-pack]');
      this.optionSelects = Array.from(this.querySelectorAll('[data-cart-bar-option]'));
      this.purchaseFieldset = this.querySelector('[data-cart-bar-purchase]');
      this.purchaseInputs = Array.from(this.querySelectorAll('[data-cart-bar-purchase-input]'));
      this.button = this.querySelector('[data-cart-bar-add-to-cart]');
      this.buttonText = this.querySelector('[data-cart-bar-button-text]');
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.variant = {price: Number(this.dataset.price) || 0, compare_at_price: Number(this.dataset.comparePrice) || 0};

      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onStateChange = (event) => {
        if (this.section.contains(event.target)) this.render();
      };
      this.onQuantityChange = (event) => {
        if (event.target.matches?.('[data-quantity-field]') && !this.contains(event.target)) this.render();
      };
      this.onLayoutChange = () => this.updateLayout();
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateOffset();
        });
      };

      this.addEventListener('change', this.onChange);
      this.button?.addEventListener('click', this.onClick);
      document.addEventListener('purchase-options:change', this.onStateChange);
      document.addEventListener('bar-packs:change', this.onStateChange);
      this.section.addEventListener('change', this.onQuantityChange);
      this.desktopQuery.addEventListener?.('change', this.onLayoutChange);
      window.addEventListener('resize', this.onScroll, {passive: true});

      if (window.subscribe && window.theme?.PUB_SUB_EVENTS) {
        this.variantUnsubscriber = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({data} = {}) => {
          const sectionId = data?.sectionId;
          if (!data?.variant || (sectionId !== this.section.dataset.section && sectionId !== this.section.dataset.originalSection)) return;
          this.variant = data.variant;
          this.updateImage(data.variant);
          this.updateOptionSelects(data.variant);
          this.render();
        });
      }

      if ('ResizeObserver' in window && this.inner) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.resizeObserver.observe(this.inner);
      }

      this.observeMainButton();
      this.watchMainButtonVisibility();
      this.updateLayout();
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('purchase-options:change', this.onStateChange);
      document.removeEventListener('bar-packs:change', this.onStateChange);
      this.section?.removeEventListener('change', this.onQuantityChange);
      this.desktopQuery?.removeEventListener?.('change', this.onLayoutChange);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('scroll', this.onScroll);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.variantUnsubscriber?.();
      this.resizeObserver?.disconnect();
      this.buttonObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      if (this.onVisibilityChange) {
        window.removeEventListener('scroll', this.onVisibilityChange);
        window.removeEventListener('resize', this.onVisibilityChange);
      }

      // Keep the page state when a product swap already connected the replacement bar
      if (!document.querySelector('cart-bar.cart-bar')) {
        document.body.classList.remove('has-cart-bar', 'has-cart-bar--top', 'has-cart-bar--bottom', 'cart-bar-is-hidden');
        document.documentElement.style.removeProperty('--cart-bar-height');
      }
    }

    get barPacks() {
      return this.section.querySelector('bar-packs');
    }

    get purchaseOptions() {
      return this.section.querySelector('purchase-options');
    }

    get mainButton() {
      return this.form.querySelector('[data-add-to-cart]') || this.form.querySelector('[type="submit"]');
    }

    get mainQuantity() {
      const input = Array.from(this.section.querySelectorAll('[data-quantity-field]')).find((field) => !this.contains(field));
      return Math.max(1, parseInt(input?.value, 10) || 1);
    }

    get isTop() {
      return this.dataset.desktopPosition !== 'bottom' && this.desktopQuery.matches;
    }

    /* Layout */

    updateLayout() {
      const isTop = this.isTop;
      this.classList.toggle('cart-bar--top', isTop);
      this.classList.toggle('cart-bar--bottom', !isTop);
      document.body.classList.add('has-cart-bar');
      document.body.classList.toggle('has-cart-bar--top', isTop);
      document.body.classList.toggle('has-cart-bar--bottom', !isTop);

      window.removeEventListener('scroll', this.onScroll);
      if (isTop) {
        this.headerGroup = document.querySelector('.header-group');
        this.stickyHeader = document.querySelector('[data-header-sticky]');
        const headerZ = parseInt(getComputedStyle(this.headerGroup?.querySelector('.shopify-section-header') || document.body).zIndex, 10);
        // Under the header while docked below it, so header dropdowns open over the bar
        if (headerZ > 0) this.style.setProperty('--cart-bar-docked-z', headerZ - 1);
        window.addEventListener('scroll', this.onScroll, {passive: true});
      } else {
        this.style.removeProperty('--cart-bar-top');
        this.classList.remove('is-docked');
      }

      this.updateHeight();
      this.updateOffset();
    }

    updateHeight() {
      if (!this.inner) return;
      const height = Math.round(this.inner.getBoundingClientRect().height);
      if (height) document.documentElement.style.setProperty('--cart-bar-height', `${height}px`);
    }

    // Desktop top: sit right under the header (and announcement bar) until it scrolls away, then pin to the top
    updateOffset() {
      if (!this.isTop) return;
      const bottoms = [this.headerGroup, this.stickyHeader].filter(Boolean).map((element) => element.getBoundingClientRect().bottom);
      const top = Math.max(0, Math.round(Math.max(0, ...bottoms)));
      if (top === this.lastTop) return;
      this.lastTop = top;
      this.style.setProperty('--cart-bar-top', `${top}px`);
      this.classList.toggle('is-docked', top > 0);
    }

    /* State */

    render() {
      this.renderPacks();
      this.renderPurchase();
      this.renderPrices();
    }

    visiblePackOptions(barPacks) {
      const options = barPacks?.data?.options || [];
      return options.filter((option) => {
        const card = barPacks.querySelector(`[data-bar-pack-option][data-key="${CSS.escape(String(option.key))}"]`);
        return card && !card.hidden;
      });
    }

    renderPacks() {
      if (!this.packSelect) return;

      const barPacks = this.barPacks;
      if (barPacks && !barPacks.data) return; // not upgraded yet: keep the server-rendered choices
      const options = this.visiblePackOptions(barPacks);
      const state = barPacks?.state;

      this.packWrapper.hidden = options.length === 0;
      // The pack selector replaces the pack size option select
      this.optionSelects.forEach((select) => {
        select.closest('[data-cart-bar-option-wrapper]').hidden = options.length > 0;
      });
      if (!options.length) return;

      const signature = options.map((option) => `${option.key}:${option.label}`).join('|');
      if (this.packSelect.dataset.signature !== signature) {
        this.packSelect.dataset.signature = signature;
        this.packSelect.innerHTML = '';
        options.forEach((option) => {
          const card = barPacks.querySelector(`[data-bar-pack-option][data-key="${CSS.escape(String(option.key))}"]`);
          const soldOut = card?.classList.contains('is-sold-out');
          const text = soldOut ? `${option.label} - ${window.theme?.strings?.soldOut || 'Sold out'}` : option.label;
          this.packSelect.add(new Option(text, option.key));
        });
      }

      const selectedKey = state?.key ?? barPacks.querySelector('[data-bar-pack-input]:checked')?.closest('[data-bar-pack-option]')?.dataset.key;
      if (selectedKey !== undefined && this.packSelect.value !== String(selectedKey)) this.packSelect.value = String(selectedKey);
    }

    renderPurchase() {
      if (!this.purchaseFieldset) return;

      const purchaseOptions = this.purchaseOptions;
      const state = purchaseOptions?.state;
      if (!purchaseOptions || !state) {
        this.purchaseFieldset.hidden = !purchaseOptions;
        return;
      }

      // No selling plan for this variant: nothing to toggle
      this.purchaseFieldset.hidden = !state.subscriptionPrice || purchaseOptions.data?.requiresSellingPlan;
      this.purchaseInputs.forEach((input) => {
        input.checked = input.value === state.purchaseType;
      });

      const discount = this.querySelector('[data-cart-bar-discount]');
      if (discount) {
        discount.hidden = !state.discountPercent;
        discount.textContent = fill(this.dataset.saveLabel || '-{percent}', {percent: `${state.discountPercent}%`});
      }
    }

    // Pack selector first: it prices the selected pack (same as its cards) even while the page switches away from a
    // hidden pack size on load. null = a price source is not ready yet, keep the server-rendered prices.
    getPrices() {
      const purchaseOptions = this.purchaseOptions;
      const purchase = purchaseOptions?.state;
      const barPacks = this.barPacks;
      const pack = barPacks?.state;
      let price;
      let compareAtPrice;

      if (pack) {
        price = pack.price;
        compareAtPrice = pack.compareAtPrice;
      } else if (barPacks || (purchaseOptions && !purchase)) {
        return null;
      } else if (purchase && purchase.price !== null) {
        const quantity = purchase.quantity || 1;
        price = purchase.totalPrice ?? purchase.price * quantity;
        compareAtPrice = purchase.totalCompareAtPrice ?? purchase.compareAtPrice * quantity;
      } else {
        const quantity = this.mainQuantity;
        price = this.variant.price * quantity;
        compareAtPrice = (this.variant.compare_at_price || 0) * quantity;
      }

      const bars = pack?.bars || 0;
      return {
        price,
        compareAtPrice: compareAtPrice > price ? compareAtPrice : 0,
        pricePerBar: bars ? Math.round(price / bars) : null,
      };
    }

    renderPrices() {
      const prices = this.getPrices();
      if (!prices) return;
      const {price, compareAtPrice, pricePerBar} = prices;

      const priceElement = this.querySelector('[data-cart-bar-price]');
      if (priceElement) priceElement.textContent = price === 0 ? window.theme?.strings?.free || 'Free' : formatMoney(price);

      const compareElement = this.querySelector('[data-cart-bar-compare]');
      const compareText = this.querySelector('[data-cart-bar-compare-text]');
      if (compareElement) {
        compareElement.hidden = !compareAtPrice;
        if (compareAtPrice) compareElement.textContent = formatMoney(compareAtPrice);
      }
      if (compareText) compareText.hidden = !compareAtPrice;

      const perBarText = pricePerBar !== null ? fill(this.dataset.perBarLabel || '{price}/bar', {price: formatMoney(pricePerBar)}) : '';
      const perBar = this.querySelector('[data-cart-bar-per-bar]');
      if (perBar) {
        perBar.hidden = !perBarText;
        perBar.textContent = perBarText;
      }
    }

    /* Main form sync */

    handleChange(event) {
      const target = event.target;

      if (target === this.packSelect) {
        this.selectPack(target.value);
      } else if (target.matches('[data-cart-bar-purchase-input]')) {
        this.purchaseOptions?.setPurchaseType?.(target.value);
      } else if (target.matches('[data-cart-bar-option]')) {
        this.selectOptionValue(target.selectedOptions[0]?.dataset.optionValueId);
      }
    }

    selectPack(key) {
      const card = this.barPacks?.querySelector(`[data-bar-pack-option][data-key="${CSS.escape(String(key))}"]`);
      const input = card?.querySelector('[data-bar-pack-input]');
      if (!input || input.checked) return;

      input.checked = true;
      const event = new Event('change', {bubbles: true});
      event.fromCartBar = true; // theme flag: no focus/scroll jump after the variant update
      input.dispatchEvent(event);
    }

    selectOptionValue(optionValueId) {
      if (!optionValueId) return;
      const target = this.section.querySelector(`variant-selects [data-option-value-id="${CSS.escape(optionValueId)}"]`);
      if (!target) return;

      if (target.matches('input')) {
        target.checked = true;
        const event = new Event('change', {bubbles: true});
        event.fromCartBar = true;
        target.dispatchEvent(event);
        return;
      }

      const click = new MouseEvent('click', {bubbles: true, cancelable: true});
      click.fromCartBar = true;
      (target.matches('a, button, [data-popout-option]') ? target : target.querySelector('a, button, [data-popout-option]') || target).dispatchEvent(click);
    }

    updateOptionSelects(variant) {
      this.optionSelects.forEach((select) => {
        const value = variant.options?.[Number(select.dataset.index) - 1];
        if (value !== undefined && select.value !== value) select.value = value;
      });
    }

    updateImage(variant) {
      const image = this.querySelector('[data-cart-bar-image] img');
      const src = variant?.featured_media?.preview_image?.src;
      if (image && src) image.src = `${src}${src.includes('?') ? '&' : '?'}width=120`;
    }

    handleClick(event) {
      event.preventDefault();
      const mainButton = this.mainButton;
      if (!mainButton || mainButton.disabled || mainButton.classList.contains('is-loading')) return;

      if (this.form.requestSubmit) {
        this.form.requestSubmit(mainButton);
      } else {
        mainButton.click();
      }
    }

    // Hide the bar while the main add to cart button is on screen
    watchMainButtonVisibility() {
      const button = this.mainButton;
      if (!button || !('IntersectionObserver' in window)) {
        this.setConcealed(false);
        return;
      }

      const inView = () => {
        const rect = button.getBoundingClientRect();
        const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        return visible > rect.height / 2;
      };

      this.onVisibilityChange = () => this.setConcealed(inView());
      this.visibilityObserver = new IntersectionObserver(this.onVisibilityChange, {threshold: [0, 0.5, 1]});
      this.visibilityObserver.observe(button);
      window.addEventListener('scroll', this.onVisibilityChange, {passive: true});
      window.addEventListener('resize', this.onVisibilityChange, {passive: true});
      this.onVisibilityChange();
    }

    setConcealed(concealed) {
      if (this.classList.contains('is-hidden') === concealed && document.body.classList.contains('cart-bar-is-hidden') === concealed) return;
      this.classList.toggle('is-hidden', concealed);
      this.toggleAttribute('inert', concealed);
      if (concealed) this.setAttribute('aria-hidden', 'true');
      else this.removeAttribute('aria-hidden');
      document.body.classList.toggle('cart-bar-is-hidden', concealed);
    }

    // Label, disabled and loading state follow the main add to cart button (updated by product-info / product-form)
    observeMainButton() {
      const mainButton = this.mainButton;
      if (!mainButton || !this.button) return;

      this.buttonObserver = new MutationObserver(() => this.mirrorMainButton());
      this.buttonObserver.observe(mainButton, {attributes: true, attributeFilter: ['class', 'disabled'], childList: true, subtree: true, characterData: true});
      this.mirrorMainButton();
    }

    mirrorMainButton() {
      const mainButton = this.mainButton;
      if (!mainButton) return;

      const text = mainButton.querySelector('[data-add-to-cart-text]')?.textContent.trim();
      if (text && this.buttonText.textContent !== text) this.buttonText.textContent = text;

      const isLoading = mainButton.classList.contains('is-loading');
      this.button.classList.toggle('is-loading', isLoading);
      this.button.classList.toggle('is-success', mainButton.classList.contains('is-success'));
      this.button.setAttribute('aria-busy', String(isLoading));
      this.button.disabled = mainButton.disabled && !isLoading && !mainButton.classList.contains('is-success');
    }
  }

  customElements.define('cart-bar', CartBar);
})();
